<template>
  <div class="relative md:h-[650px]">
    <div v-if="loading" class="h-full w-full">
      <TheLoader class="h-16 my-10" />
    </div>
    <div
      v-else-if="articles.length > 0"
      class="grid grid-cols-1 gap-x-4 gap-y-6 md:h-[650px] md:grid-cols-2"
      data-testid="source-articles-content"
    >
      <NavigationLink
        :url="firstArticle.url ?? ''"
        class="bg-grey-900 flex cursor-pointer flex-col rounded-xl mt-2"
        data-testid="source-hero-article-card"
        type="a"
      >
        <div
          :style="{
            backgroundImage: `url('${firstArticle?.backgroundImage}')`,
          }"
          class="min-h-44 flex-1 bg-cover bg-center rounded-t-xl"
          data-testid="article-card-image"
        />
        <div class="flex min-h-[300px] flex-col justify-between p-6 text-white">
          <div>
            <div class="text-grey-600 flex flex-col text-xs font-semibold">
              <span class="hidden md:block" data-testid="article-card-type">
                Article
              </span>
              <span data-testid="article-card-published-date">
                {{ firstArticle?.publishedDate }}
                {{
                  firstArticle?.readingTime
                    ? `| ${firstArticle.readingTime} min read`
                    : ''
                }}
              </span>
            </div>
            <h3
              class="mb-3 mt-2 line-clamp-2 text-lg font-bold leading-normal md:text-4xl md:leading-[38px]"
              data-testid="article-card-title"
            >
              {{ firstArticle?.title }}
            </h3>
            <p
              class="mb-11 line-clamp-3 text-base"
              data-testid="article-card-description"
              v-html="firstArticle?.excerpt"
            />
          </div>
          <div class="flex flex-wrap gap-3">
            <span
              v-for="category in firstArticle?.categories"
              :key="category.id"
              class="bg-p-500 text-grey-900 md:text-xxs rounded-full px-2 py-1.5 text-sm font-semibold leading-normal"
              data-testid="article-card-pills"
            >
              {{ category.name }}
            </span>
          </div>
        </div>
      </NavigationLink>
      <div
        class="md:flex flex-col overflow-hidden md:max-h-[650px] md:overflow-y-auto hidden pr-2 pt-2"
      >
        <SourceArticleCard
          v-for="article in articles"
          :key="article.slug"
          data-testid="source-article-desktop-card"
          :background-image="article.backgroundImage"
          :published-date="article.publishedDate"
          :reading-time="article.readingTime"
          :title="article.title"
          :url="article.url"
        />
      </div>
      <div class="relative w-full overflow-hidden md:hidden">
        <Swiper
          :slides-per-view="3"
          :space-between="16"
          :speed="500"
          class="w-[956px]"
        >
          <SwiperSlide
            v-for="(swArticle, i) in articles"
            :key="i"
            class="w-[308px]"
          >
            <SourceArticleCard
              :key="swArticle.slug"
              :background-image="swArticle.backgroundImage"
              :published-date="swArticle.publishedDate"
              :reading-time="swArticle.readingTime"
              :title="swArticle.title"
              :url="swArticle.url"
            />
          </SwiperSlide>
          <SwiperSlide class="w-[308px]"></SwiperSlide>
          <SwiperSlide class="w-[308px]"></SwiperSlide>
        </Swiper>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import SourceArticleCard from '@/components/bm-source/SourceArticleCard.vue'
import 'swiper/css'
import { Swiper, SwiperSlide } from 'swiper/vue'
import { computed, onMounted, ref } from 'vue'
import NavigationLink from '~/components/shared/navigation/Link.vue'
import type { TGeneral } from '~/src/types/common'

const props = withDefaults(
  defineProps<{
    market: string | string[]
    categoriesArticles?: string[]
  }>(),
  {
    categoriesArticles: null,
  },
)

const { $graphqlClient } = useNuxtApp()
const loading = ref<boolean>(true)
const firstArticle = ref<TGeneral | null>(null)
const articles = ref<TGeneral[]>([])

const categories = computed(() => props.categoriesArticles ?? null)

const fetchNews = async (): Promise<void> => {
  try {
    loading.value = true
    const news: TGeneral[] = await $graphqlClient.getNewsArticles({
      market: props.market,
      categories: categories.value,
    })
    if (news.length) {
      firstArticle.value = news[0]
      articles.value = news.slice(1)
    }
  } finally {
    loading.value = false
  }
}

onMounted(fetchNews)
</script>
